import { Backdrop } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import { Theme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import { createStyles, makeStyles } from "@material-ui/styles";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useEffect } from "react";
import { store } from "../../../..";
import { checkTransactionStatus, clearCart, getCart, setCart } from "../../../../api/CartApi";
import { TransactionStatus } from "../../../../model/Cart/TransactionStatus";
import { ModuleSelectionViewModel } from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import ErrorHelper from "../../../../utils/ErrorHelper";
import { useQuery } from "../../../../utils/UseQuery";
import { LoadingSpinner } from "../../../common/LoadingSpinner";
import TranslatedMessage from "../../../translations/TranslatedMessage";
import { useSignUpContext } from "../../CertificationDataWizardContext/CertificationDataWizardContext";
import { ControlButtons } from "../shared/ControlButtons";
import FaqLink from "./FaqLink";
import SelectionSummary from "./SelectionSummary";
import { isTranslationKey, TranslationKey } from "../../../../translations/TranslationKey";
import { CourseType } from "../../../../model/Course";
import { GetTranslation } from "../../../../translations/GetTranslation";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { UrlQueryParameters } from "../../../../data/constants";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "90%",
      marginLeft: "auto",
      marginRight: "auto",
    },
    paper: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(5),
      width: "100%",
      overflowX: "auto",
      marginBottom: theme.spacing(2),
    },
    errors: {
      marginLeft: "auto",
      marginRight: "auto",
      padding: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
    confirmMessage: {
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(4),
    },
    backDrop: {
      zIndex: theme.zIndex.tooltip,
    },
  })
);

type WaitStates =
  | "init"
  | "initDone"
  | "waitingTransactionConfirmation"
  | "waitingForCart"
  | "cartUpdated"
  | "paymentError"
  | "finished";

const PaymentAndCompletionStep: React.FunctionComponent = () => {
  const { handlePrevStep, handleNextStep, data, setValues, isLoading } = useSignUpContext();

  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const datatransTrxId = useQuery().get(UrlQueryParameters.datatransTrxId);
  const classes = useStyles({});
  const [waitingForOrderPaymentConfirmation, setWaitingForOrderPaymentConfirmation] = React.useState(false);
  const [state, setState] = React.useState<WaitStates>("init");
  const [confirmationError, setConfirmationError] = React.useState(false);
  const [isCartSubmitting, setIsCartSubmitting] = React.useState(false);
  const [checkStatusTimeout, setCheckStatusTimeout] = React.useState(1000);
  const [moduleErrors, setModuleErrors] = React.useState<string[]>([]);
  const history = useHistory();

  const sessionId = store.getState().sessionState.sessionId;

  const getModuleByTransactionId = (transactionId: string): ModuleSelectionViewModel | undefined => {
    return data.modulesSelection.find((m) => m.transactionId === transactionId);
  };

  const refreshCart = () => {
    setIsCartSubmitting(true);
    getCart(!datatransTrxId)
      .then((cart) => {
        setValues({ modulesSelection: cart!.modules });
        if (state !== "paymentError" && state !== "finished") {
          setState("cartUpdated");
        } else {
          setState("finished");
        }
      })
      .finally(() => {
        setIsCartSubmitting(false);
      });
  };

  const scheduleNextTransactionUpdate = () => {
    setState("waitingTransactionConfirmation");
    setTimeout(() => getTransactionStatus(), checkStatusTimeout);
    if (checkStatusTimeout < 60000) {
      setCheckStatusTimeout((prevTimeout) => prevTimeout + 1000);
    }
  };

  const updateCartWithModulesSelection = (modules?: ModuleSelectionViewModel[]) => {
    if (!datatransTrxId || modules !== undefined) {
      if (!modules || modules.length !== data.modulesSelection.length) {
        setCart({ candidateId: data.candidate.id, modules: modules ?? data.modulesSelection }).then((cart) => {
          if (cart?.modules) {
            setValues({ modulesSelection: cart!.modules });
          }
          if (!datatransTrxId) {
            setState("finished");
          }
        });
      }
    }
  };

  const getTransactionStatus = () => {
    if (datatransTrxId) {
      checkTransactionStatus(datatransTrxId)
        .then((transaction) => {
          if (transaction.status === TransactionStatus.Paid) {
            setState("waitingForCart");
            refreshCart();
            enqueueSnackbar(
              <TranslatedMessage
                id="startCertification.paymentAndCompletionStep.messages.transactionPaid"
                values={{ id: datatransTrxId }}
              />,
              { variant: "success", key: transaction.transactionId }
            );
            setWaitingForOrderPaymentConfirmation(false);
            setState("finished");
          } else if (transaction.status === TransactionStatus.Initialized) {
            scheduleNextTransactionUpdate();
          } else if (transaction.status === TransactionStatus.Cancelled) {
            enqueueSnackbar(
              <TranslatedMessage
                id="startCertification.paymentAndCompletionStep.messages.transactionCancelled"
                values={{ id: datatransTrxId }}
              />,
              { variant: "warning", key: transaction.transactionId }
            );
            setWaitingForOrderPaymentConfirmation(false);
            setState("finished");
          } else {
            enqueueSnackbar(
              <span>
                <TranslatedMessage
                  id="startCertification.paymentAndCompletionStep.messages.transactionFailed"
                  values={{ id: datatransTrxId }}
                />{" "}
                <span>
                  {isTranslationKey(transaction.message) ? (
                    <TranslatedMessage id={transaction.message as TranslationKey} />
                  ) : (
                    transaction.message
                  )}
                </span>
              </span>,
              { variant: "error", key: transaction.transactionId }
            );
            setWaitingForOrderPaymentConfirmation(false);
            setState("finished");
          }
        })
        .catch((err) => {
          setConfirmationError(true);
          setState("finished");
        });
    }
  };

  useEffect(() => {
    setModuleErrors(ErrorHelper.GetModuleErrors(data.modulesSelection));
  }, [data.modulesSelection]);

  useEffect(() => {
    if (state === "init" || state === "initDone" || state === "waitingForCart" || state === "cartUpdated") {
      if (state === "init") {
        setState("initDone");
        updateCartWithModulesSelection();
        if (datatransTrxId) {
          setWaitingForOrderPaymentConfirmation(true);
          scheduleNextTransactionUpdate();
        }
      }
      if (datatransTrxId && !isCartSubmitting && state === "cartUpdated") {
        var module = getModuleByTransactionId(datatransTrxId);
        if (module && !module.paid) {
          setWaitingForOrderPaymentConfirmation(true);
          scheduleNextTransactionUpdate();
        } else {
          setWaitingForOrderPaymentConfirmation(false);
          setState("finished");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.modulesSelection, state, isCartSubmitting]);

  useEffect(() => {
    if (!document.getElementById("tmx_tags_iframe")) {
      // const script = document.createElement("script");
      // script.type = "text/javascript";
      // script.src = `https://h.online-metrix.net/fp/tags.js?org_id=lq866c5i&session_id=${sessionId}`;
      // script.async = true;

      // document.body.appendChild(script);

      const iframe = document.createElement("iframe");
      iframe.innerHTML = `<iframe iid="tmx_tags_iframe" style="width: 100px; height: 100px; border: 0; position: absolute; top: -5000px;" data-sessionId={${sessionId}}
    src="https://h.online-metrix.net/tags?org_id=lq866c5i&session_id=${sessionId}""></iframe>`;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitRegistration = async () => {
    clearCart().then(() => {
      handleNextStep();
    });
  };

  const handleOnPreviousClick = () => {
    if (datatransTrxId) {
      history.replace("/startCertification");
    }
    handlePrevStep();
  };

  const handleRemoveModule = (module: ModuleSelectionViewModel) => {
    const modules = data.modulesSelection.filter(
      (m) =>
        m.courseId !== module.courseId || m.examSessionId !== module.examSessionId || m.moduleId !== module.moduleId
    );
    updateCartWithModulesSelection(modules);
  };

  const handlePayModule = (module: ModuleSelectionViewModel) => {
    const modulesSelection = [...data.modulesSelection];
    const paidModule = data.modulesSelection.find(
      (m) =>
        m.moduleId === module.moduleId && m.courseId === module.courseId && m.examSessionId === module.examSessionId
    );
    if (paidModule) {
      paidModule.paid = true;
    }
    setValues({ modulesSelection });
  };

  const allModulesPaid = !data?.modulesSelection?.find((m) => !m.paid);

  const busy = state !== "finished" || isCartSubmitting || !data || isLoading || waitingForOrderPaymentConfirmation;

  return (
    <React.Fragment>
      <SelectionSummary
        onRemoveModule={handleRemoveModule}
        onPayModule={handlePayModule}
        onPaymentError={() => {
          setState("paymentError");
          refreshCart();
        }}
        isLoading={busy}
      />

      {moduleErrors.length > 0 && (
        <Alert severity="error" className={classes.errors}>
          <AlertTitle>
            <TranslatedMessage id="examSession.validation.someErrorsOccurred" />
          </AlertTitle>
          {moduleErrors.map((err) => (
            <TranslatedMessage key={err} id={err as TranslationKey} />
          ))}
        </Alert>
      )}

      <Grid container spacing={4} className={classes.root}>
        <Paper variant="outlined" className={classes.paper}>
          <Typography component="div">
            <TranslatedMessage id="startCertification.paymentAndCompletionStep.generalInfo.freeCancellation" />
          </Typography>
          <Typography component="div">
            <TranslatedMessage id="startCertification.paymentAndCompletionStep.generalInfo.cost1" />
            <b>
              <TranslatedMessage id="startCertification.paymentAndCompletionStep.generalInfo.cost2bold" />
            </b>
            <TranslatedMessage id="startCertification.paymentAndCompletionStep.generalInfo.cost3" />
          </Typography>
          {data.modulesSelection?.some((m) => m.courseType === CourseType.Hve) ? (
            <FaqLink
              link={GetTranslation(intl, "startCertification.paymentAndCompletionStep.generalInfo.hveFAQLink")}
              linkText={GetTranslation(intl, "startCertification.paymentAndCompletionStep.generalInfo.hveFAQLinkText")}
            />
          ) : null}
          {data.modulesSelection?.some((m) => m.courseType === CourseType.Intermediary) ? (
            <FaqLink
              link={GetTranslation(intl, "startCertification.paymentAndCompletionStep.generalInfo.intermediaryFAQLink")}
              linkText={GetTranslation(
                intl,
                "startCertification.paymentAndCompletionStep.generalInfo.intermediaryFAQLinkText"
              )}
            />
          ) : null}
        </Paper>
      </Grid>
      <ControlButtons
        onPreviousClick={handleOnPreviousClick}
        onNextClick={submitRegistration}
        isLoading={isCartSubmitting}
        nextDisabled={!allModulesPaid || data.modulesSelection.length === 0}
      />
      {!datatransTrxId && state !== "finished" && <LoadingSpinner />}

      {waitingForOrderPaymentConfirmation && (
        <>
          {!confirmationError && (
            <LoadingSpinner>
              <Paper className={classes.confirmMessage}>
                <h3>
                  <TranslatedMessage id="startCertification.paymentAndCompletionStep.paymentConfirmationTitle" />
                </h3>
                <p>
                  <TranslatedMessage id="startCertification.paymentAndCompletionStep.paymentConfirmationMessagePart1" />
                  <br />
                  <TranslatedMessage
                    id="startCertification.paymentAndCompletionStep.paymentConfirmationMessagePart2"
                    values={{ datatransTrxId }}
                  />
                </p>
              </Paper>
            </LoadingSpinner>
          )}
          {confirmationError && (
            <Backdrop open={true} className={classes.backDrop}>
              <Paper className={classes.confirmMessage}>
                <Alert severity="error" onClose={() => setWaitingForOrderPaymentConfirmation(false)}>
                  <TranslatedMessage
                    id="startCertification.paymentAndCompletionStep.messages.wrongTransactionId"
                    values={{ id: datatransTrxId }}
                  />
                </Alert>
              </Paper>
            </Backdrop>
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default PaymentAndCompletionStep;
